import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { quotes } from '../data/quotes';
import {
  generateCipher,
  normalizeText,
  encodeText,
  getUsedCipherLetters,
  isSolved,
  hasShortWord,
  GERMAN_ALPHABET,
  type CipherMap,
} from '../utils/cryptogram';
import './CryptogramGame.css';

const PLAYABLE_QUOTES = quotes.filter((q) => hasShortWord(q.text));

const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Z', 'U', 'I', 'O', 'P', 'Ü'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä'],
  ['Y', 'X', 'C', 'V', 'B', 'N', 'M', '⌫'],
];

const BEST_TIME_KEY = 'cg-best-time';

interface PuzzleState {
  quoteIndex: number;
  plainText: string;
  encodedText: string;
  cipher: CipherMap;
  usedCipherLetters: Set<string>;
}

function buildPuzzle(quoteIndex: number): PuzzleState {
  const quote = PLAYABLE_QUOTES[quoteIndex];
  const plainText = normalizeText(quote.text);
  const cipher = generateCipher();
  const encodedText = encodeText(plainText, cipher);
  const usedCipherLetters = getUsedCipherLetters(encodedText);
  return { quoteIndex, plainText, encodedText, cipher, usedCipherLetters };
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function CryptogramGame() {
  const [puzzle, setPuzzle] = useState<PuzzleState>(() =>
    buildPuzzle(Math.floor(Math.random() * PLAYABLE_QUOTES.length))
  );
  const [userMapping, setUserMapping] = useState<Record<string, string>>({});
  const [selectedCipherLetter, setSelectedCipherLetter] = useState<string | null>(null);
  const [solved, setSolved] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [revealedLetters, setRevealedLetters] = useState<Set<string>>(new Set());
  const [wrongAttempt, setWrongAttempt] = useState(false);

  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isNewBest, setIsNewBest] = useState(false);
  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Text-position-based cursor: tracks physical index in the letter sequence
  const cursorPosRef = useRef<number>(-1);
  // Tracks the last position acted on (typed into or clicked) — used by backspace
  const lastTouchedPosRef = useRef<number>(-1);

  // Flat array of cipher letters in text reading order (with duplicates)
  const textLetters = useMemo(() => {
    const result: string[] = [];
    for (const ch of puzzle.encodedText) {
      const upper = ch.toUpperCase();
      if (GERMAN_ALPHABET.includes(upper)) result.push(upper);
    }
    return result;
  }, [puzzle.encodedText]);

  // Start timer on new puzzle
  useEffect(() => {
    startTimeRef.current = Date.now();
    setElapsedSeconds(0);
    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [puzzle]);

  useEffect(() => {
    containerRef.current?.focus();
  }, [puzzle]);

  const handleNewPuzzle = useCallback(() => {
    const idx = Math.floor(Math.random() * PLAYABLE_QUOTES.length);
    setPuzzle(buildPuzzle(idx));
    setUserMapping({});
    setSelectedCipherLetter(null);
    setSolved(false);
    setHintsUsed(0);
    setRevealedLetters(new Set());
    setIsNewBest(false);
    setWrongAttempt(false);
    cursorPosRef.current = -1;
    lastTouchedPosRef.current = -1;
  }, []);

  const handleReset = useCallback(() => {
    setUserMapping({});
    setSelectedCipherLetter(null);
    setHintsUsed(0);
    setRevealedLetters(new Set());
    setWrongAttempt(false);
    startTimeRef.current = Date.now();
    setElapsedSeconds(0);
    cursorPosRef.current = -1;
    lastTouchedPosRef.current = -1;
  }, []);

  // Scan forward from fromPos for the next unfilled, non-revealed cipher letter
  const advanceSelection = useCallback(
    (fromPos: number, currentMapping: Record<string, string>) => {
      // Forward pass
      for (let i = fromPos + 1; i < textLetters.length; i++) {
        const cl = textLetters[i];
        if (!revealedLetters.has(cl) && !currentMapping[cl]) {
          cursorPosRef.current = i;
          setSelectedCipherLetter(cl);
          return;
        }
      }
      // Wrap from beginning
      for (let i = 0; i <= fromPos; i++) {
        const cl = textLetters[i];
        if (!revealedLetters.has(cl) && !currentMapping[cl]) {
          cursorPosRef.current = i;
          setSelectedCipherLetter(cl);
          return;
        }
      }
      // All filled — stay put
    },
    [textLetters, revealedLetters]
  );

  const refocus = useCallback(() => {
    containerRef.current?.focus();
  }, []);

  const handleLetterInput = useCallback(
    (letter: string) => {
      if (!selectedCipherLetter || solved) return;
      const upper = letter.toUpperCase();
      if (!GERMAN_ALPHABET.includes(upper)) return;

      const fromPos = cursorPosRef.current;
      lastTouchedPosRef.current = fromPos;

      const newMap = { ...userMapping };
      newMap[selectedCipherLetter] = upper;
      setUserMapping(newMap);
      advanceSelection(fromPos, newMap);
      refocus();
    },
    [selectedCipherLetter, solved, userMapping, advanceSelection, refocus]
  );

  const handleBackspace = useCallback(() => {
    if (solved) return;

    const fromPos = lastTouchedPosRef.current >= 0 ? lastTouchedPosRef.current : cursorPosRef.current;

    // Scan backwards for the nearest position that has a guess and isn't revealed
    let targetPos = -1;
    for (let i = fromPos; i >= 0; i--) {
      const cl = textLetters[i];
      if (!revealedLetters.has(cl) && userMapping[cl]) {
        targetPos = i;
        break;
      }
    }
    if (targetPos < 0) { refocus(); return; }

    const target = textLetters[targetPos];
    setUserMapping((m) => { const n = { ...m }; delete n[target]; return n; });
    setSelectedCipherLetter(target);
    cursorPosRef.current = targetPos;

    // Set lastTouched to the nearest previous filled position (for the next backspace)
    for (let i = targetPos - 1; i >= 0; i--) {
      const cl = textLetters[i];
      if (!revealedLetters.has(cl) && userMapping[cl]) {
        lastTouchedPosRef.current = i;
        return;
      }
    }
    lastTouchedPosRef.current = -1;
    refocus();
  }, [solved, textLetters, revealedLetters, userMapping, refocus]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Backspace') { handleBackspace(); return; }
      if (e.key === 'Enter' && selectedCipherLetter) {
        advanceSelection(cursorPosRef.current, userMapping);
        return;
      }
      if (e.key.length === 1) handleLetterInput(e.key);
    },
    [handleLetterInput, handleBackspace, advanceSelection, selectedCipherLetter, userMapping]
  );

  // Check solved
  useEffect(() => {
    if (solved) return;
    const allFilled = [...puzzle.usedCipherLetters].every((cl) => userMapping[cl]);
    if (!allFilled) { setWrongAttempt(false); return; }

    if (isSolved(userMapping, puzzle.cipher, puzzle.usedCipherLetters)) {
      if (timerRef.current) clearInterval(timerRef.current);
      const finalTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsedSeconds(finalTime);
      const stored = localStorage.getItem(BEST_TIME_KEY);
      const best = stored ? parseInt(stored, 10) : Infinity;
      if (finalTime < best) {
        localStorage.setItem(BEST_TIME_KEY, String(finalTime));
        setIsNewBest(true);
      }
      setSolved(true);
    } else {
      setWrongAttempt(true);
    }
  }, [userMapping, puzzle, solved]);

  const handleHint = useCallback(() => {
    const unsolved = [...puzzle.usedCipherLetters].filter(
      (cl) =>
        !revealedLetters.has(cl) &&
        userMapping[cl]?.toUpperCase() !== puzzle.cipher.decode[cl]?.toUpperCase()
    );
    if (unsolved.length === 0) return;

    const pick = unsolved[Math.floor(Math.random() * unsolved.length)];
    const plain = puzzle.cipher.decode[pick];

    setRevealedLetters((prev) => new Set([...prev, pick]));
    setUserMapping((prev) => ({ ...prev, [pick]: plain.toUpperCase() }));
    setHintsUsed((n) => n + 1);
    setSelectedCipherLetter(pick);
  }, [puzzle, userMapping, revealedLetters]);

  const handleShare = useCallback(() => {
    const quote = PLAYABLE_QUOTES[puzzle.quoteIndex];
    const text = `„${quote.text}"\n— ${quote.author}`;
    if (navigator.share) {
      navigator.share({ text });
    } else {
      navigator.clipboard.writeText(text);
    }
  }, [puzzle]);

  const quote = PLAYABLE_QUOTES[puzzle.quoteIndex];
  const tokens = tokenize(puzzle.encodedText);

  // ── Success screen ──
  if (solved) {
    return (
      <div className="cg-root" ref={containerRef} tabIndex={-1}>
        <header className="cg-header">
          <h1>Kryptogramm</h1>
        </header>
        <div className="cg-success">
          <h2 className="cg-success-heading">Sehr gut!</h2>
          <div className="cg-success-author">{quote.author}</div>
          <blockquote className="cg-success-quote">„{quote.text}"</blockquote>
          <div className="cg-success-actions">
            <button className="cg-success-btn cg-success-btn--primary" onClick={handleNewPuzzle}>
              Neues Spiel
            </button>
            <button className="cg-success-btn cg-success-btn--secondary" onClick={handleShare}>
              Zitat teilen
            </button>
          </div>
          <div className="cg-success-time">
            {isNewBest && <div className="cg-success-best">Neue Bestzeit!</div>}
            <div className="cg-success-elapsed">
              Zeit &nbsp; {formatTime(elapsedSeconds)}
              {hintsUsed > 0 && (
                <span className="cg-success-hints">
                  &nbsp;· {hintsUsed} Hinweis{hintsUsed > 1 ? 'e' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Puzzle screen ──
  // Running counter for assigning text positions to letter cells during render
  let letterPos = -1;

  return (
    <div className="cg-root" ref={containerRef} tabIndex={-1} onKeyDown={handleKeyDown}>
      <header className="cg-header">
        <h1>Kryptogramm</h1>
      </header>

      <div className="cg-body">
        <nav className="cg-sidebar">
          <button className="cg-menu-item" onClick={handleHint}>
            Hinweis
            {hintsUsed > 0 && <span className="cg-menu-badge">{hintsUsed}</span>}
          </button>
          <button className="cg-menu-item" onClick={handleReset}>
            Zurücksetzen
          </button>
          <button className="cg-menu-item" onClick={handleNewPuzzle}>
            Neues Rätsel
          </button>
        </nav>

        <main className="cg-main">
          <div className="cg-author">{quote.author}</div>

          {wrongAttempt && (
            <div className="cg-wrong-attempt">Noch nicht ganz richtig.</div>
          )}

          <div className="cg-puzzle">
            {tokens.map((token, ti) => {
              if (!isLetter(token[0])) {
                if (token === ' ') {
                  return <span key={ti} className="cg-token cg-punct">{'\u00A0'}</span>;
                }
                return (
                  <span key={ti} className="cg-cell cg-cell--punct">
                    <span className="cg-cell-input">{token}</span>
                    <span className="cg-cell-cipher">{token}</span>
                  </span>
                );
              }
              return (
                <span key={ti} className="cg-word">
                  {token.split('').map((ch, ci) => {
                    const upper = ch.toUpperCase();
                    const isAlpha = GERMAN_ALPHABET.includes(upper);
                    if (!isAlpha) {
                      return (
                        <span key={ci} className="cg-cell cg-cell--punct">
                          <span className="cg-cell-input">{ch}</span>
                          <span className="cg-cell-cipher">{ch}</span>
                        </span>
                      );
                    }

                    const pos = ++letterPos;
                    const userGuess = userMapping[upper] ?? '';
                    const isRevealed = revealedLetters.has(upper);
                    const isSelected = selectedCipherLetter === upper;

                    return (
                      <span
                        key={ci}
                        className={[
                          'cg-cell',
                          isSelected ? 'cg-cell--selected' : '',
                          isRevealed ? 'cg-cell--revealed' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => {
                          if (isRevealed) return;
                          cursorPosRef.current = pos;
                          lastTouchedPosRef.current = pos;
                          setSelectedCipherLetter(upper);
                          refocus();
                        }}
                      >
                        <span
                          className="cg-cell-input"
                          style={{ color: userGuess ? undefined : 'transparent' }}
                        >{userGuess || '\u00A0'}</span>
                        <span className="cg-cell-cipher">{ch.toUpperCase()}</span>
                      </span>
                    );
                  })}
                </span>
              );
            })}
          </div>
        </main>
      </div>

      <div className="cg-keyboard">
        {KEYBOARD_ROWS.map((row, ri) => (
          <div key={ri} className="cg-keyboard-row">
            {row.map((key) => {
              if (key === '⌫') {
                return (
                  <button key={key} className="cg-key cg-key--backspace" onClick={handleBackspace}>
                    ⌫
                  </button>
                );
              }
              const isUsed = Object.values(userMapping).some((v) => v.toUpperCase() === key);
              return (
                <button
                  key={key}
                  className={['cg-key', isUsed ? 'cg-key--used' : ''].filter(Boolean).join(' ')}
                  onClick={() => handleLetterInput(key)}
                >
                  {key}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function tokenize(text: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let inWord = false;
  for (const ch of text) {
    const alpha = GERMAN_ALPHABET.includes(ch.toUpperCase());
    if (alpha !== inWord) {
      if (current) tokens.push(current);
      current = ch;
      inWord = alpha;
    } else {
      current += ch;
    }
  }
  if (current) tokens.push(current);
  return tokens;
}

function isLetter(ch: string): boolean {
  return GERMAN_ALPHABET.includes(ch.toUpperCase());
}
